import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, getInviteCode } from "../lib/api";
import type { Artifact } from "../lib/types";
import Layout from "../components/Layout";
import ArtifactCard from "../components/ArtifactCard";

const ARTIFACT_META: Record<
  string,
  { icon: string; name: string; description: string }
> = {
  "business-case.md": {
    icon: "\uD83D\uDCCA",
    name: "Business Case",
    description: "TAM/SAM/SOM, unit economics, competitive analysis",
  },
  "product-spec.md": {
    icon: "\uD83D\uDCCB",
    name: "Product Specification",
    description: "User stories, features, acceptance criteria",
  },
  "architecture.md": {
    icon: "\uD83C\uDFD7\uFE0F",
    name: "Technical Architecture",
    description: "Tech stack, data model, API contracts",
  },
  "epic.md": {
    icon: "\uD83D\uDCD0",
    name: "Epic Plan",
    description: "Milestone breakdown, task estimates",
  },
};

function getArtifactMeta(filename: string) {
  return (
    ARTIFACT_META[filename] ?? {
      icon: "\uD83D\uDCC4",
      name: filename,
      description: "Generated artifact",
    }
  );
}

export default function Artifacts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const runId = searchParams.get("run_id");

  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [feedbackStatus, setFeedbackStatus] = useState<string | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [mvpInterest, setMvpInterest] = useState<boolean | null>(null);
  const [mvpDismissed, setMvpDismissed] = useState(false);
  const [mvpLoading, setMvpLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auth guard
  useEffect(() => {
    if (!getInviteCode()) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Redirect if no run_id
  useEffect(() => {
    if (!runId) navigate("/dashboard", { replace: true });
  }, [runId, navigate]);

  // Fetch artifacts
  useEffect(() => {
    if (!runId) return;
    setLoading(true);
    api
      .listArtifacts(runId)
      .then((res) => {
        setArtifacts(res.artifacts);
        setProjectId(res.project_id || null);
        // Fetch project detail for feedback status + mvp_interest
        if (res.project_id) {
          api.getProject(res.project_id).then((detail) => {
            setFeedbackStatus(detail.phase);
            setMvpInterest(detail.mvp_interest);
          }).catch(() => {});
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load artifacts");
      })
      .finally(() => setLoading(false));
  }, [runId]);

  // Telemetry: log artifacts_viewed
  useEffect(() => {
    if (projectId) {
      api.logEvent(projectId, "artifacts_viewed");
    }
  }, [projectId]);

  async function handleDownload(filename: string) {
    if (!runId) return;
    try {
      const content = await api.getArtifactContent(runId, filename);
      const blob = new Blob([content], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Failed to download artifact. Please try again.");
    }
  }

  async function handleDownloadAll() {
    if (!runId) return;
    for (const artifact of artifacts) {
      await handleDownload(artifact.filename);
    }
  }

  async function handleExportProject() {
    if (!projectId) return;
    setExportLoading(true);
    try {
      await api.exportProject(projectId);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to export project");
    } finally {
      setExportLoading(false);
    }
  }

  async function handleMvpInterested() {
    if (!projectId) return;
    setMvpLoading(true);
    try {
      await api.recordMvpInterest(projectId);
      setMvpInterest(true);
    } catch {
      // Silently fail — not critical
    } finally {
      setMvpLoading(false);
    }
  }

  async function handleStartFeedback() {
    if (!projectId) return;
    setFeedbackLoading(true);
    try {
      await api.startFeedback(projectId);
      navigate(`/chat?project=${projectId}&feedback=true`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to start feedback";
      setError(msg);
      setFeedbackLoading(false);
    }
  }

  return (
    <Layout showHeader>
      <div className="flex-1 overflow-y-auto px-4 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white text-center mb-2">
            Your product plan is ready!
          </h2>
          <p className="text-slate-400 text-center text-sm sm:text-base mb-8">
            Download your generated artifacts below.
          </p>

          {loading && (
            <div className="text-center py-12 text-slate-500">
              Loading artifacts...
            </div>
          )}

          {error && (
            <div className="text-center py-12 text-red-400 text-sm">
              {error}
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-3">
              {artifacts.map((artifact) => {
                const meta = getArtifactMeta(artifact.filename);
                return (
                  <ArtifactCard
                    key={artifact.filename}
                    icon={meta.icon}
                    name={meta.name}
                    description={meta.description}
                    size={artifact.size}
                    onDownload={() => handleDownload(artifact.filename)}
                  />
                );
              })}

              {artifacts.length > 1 && (
                <button
                  onClick={handleDownloadAll}
                  className="w-full bg-slate-800/60 border border-slate-700/50 hover:border-slate-600/50 text-slate-300 rounded-xl py-3 text-sm font-medium transition-colors mt-4"
                >
                  Download All
                </button>
              )}

              {projectId && (
                <button
                  onClick={handleExportProject}
                  disabled={exportLoading}
                  className="w-full bg-slate-800/60 border border-slate-700/50 hover:border-slate-600/50 text-slate-300 rounded-xl py-3 text-sm font-medium transition-colors mt-2 disabled:opacity-50"
                >
                  {exportLoading ? "Exporting..." : "Export Project as ZIP"}
                </button>
              )}
            </div>
          )}

          {/* MVP Interest card */}
          {!loading && !error && projectId && mvpInterest !== null && (
            <>
              {mvpInterest ? (
                <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border border-indigo-500/20 rounded-2xl p-5 sm:p-6 mt-6">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm text-indigo-300">
                      Thanks! We'll reach out when MVP builds are available.
                    </p>
                  </div>
                </div>
              ) : mvpDismissed ? (
                <div className="border border-slate-700/30 rounded-xl p-3 mt-6 text-center">
                  <span className="text-xs text-slate-500">
                    &#x1F680; MVP Build — coming soon
                  </span>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 rounded-2xl p-5 sm:p-6 mt-6">
                  <h3 className="text-base font-semibold text-indigo-300 mb-2">
                    &#x1F680; What's Next
                  </h3>
                  <p className="text-sm text-slate-300 mb-1">
                    You now have a complete project plan — business case, product spec,
                    architecture, and implementation roadmap.
                  </p>
                  <p className="text-sm text-slate-400 mb-4">
                    Coming soon: Architect can take this exact plan and build your
                    working MVP — deployed and ready to test with real users.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleMvpInterested}
                      disabled={mvpLoading}
                      className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors"
                    >
                      {mvpLoading ? "Recording..." : "I'm interested"}
                    </button>
                    <button
                      onClick={() => setMvpDismissed(true)}
                      className="text-sm text-slate-500 hover:text-slate-400 transition-colors"
                    >
                      Maybe later
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Feedback card */}
          {!loading && !error && projectId && feedbackStatus && !["feedback_complete"].includes(feedbackStatus) && feedbackStatus !== "feedback_active" && (
            <div className="bg-slate-800/80 border border-blue-500/30 rounded-2xl p-5 sm:p-6 mt-6">
              <h3 className="text-base font-semibold text-blue-400 mb-2">
                How was your experience?
              </h3>
              <p className="text-sm text-slate-300 mb-4">
                We'd love to hear your thoughts on the documents Architect generated.
                It's a quick conversation — not a survey.
              </p>
              <button
                onClick={handleStartFeedback}
                disabled={feedbackLoading}
                className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors"
              >
                {feedbackLoading ? "Starting..." : "Share Feedback"}
              </button>
            </div>
          )}

          {!loading && !error && feedbackStatus === "feedback_active" && projectId && (
            <div className="bg-slate-800/80 border border-blue-500/30 rounded-2xl p-5 sm:p-6 mt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-blue-400">
                  Feedback interview in progress
                </h3>
                <button
                  onClick={() => navigate(`/chat?project=${projectId}&feedback=true`)}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-4 py-2 text-sm transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {!loading && !error && feedbackStatus === "feedback_complete" && projectId && (
            <div className="bg-slate-800/80 border border-green-500/30 rounded-2xl p-5 sm:p-6 mt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <h3 className="text-base font-semibold text-green-400">
                    Feedback received — thank you!
                  </h3>
                </div>
                <button
                  onClick={() => navigate(`/chat?project=${projectId}&feedback=true`)}
                  className="border border-slate-600 hover:border-slate-500 text-slate-300 rounded-xl px-4 py-2 text-sm transition-colors"
                >
                  View Conversation
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 text-center space-y-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="border border-slate-600 hover:border-slate-500 text-slate-300 rounded-xl px-5 py-2.5 text-sm transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
